import React, { useState } from "react";

import { Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { logo } from "../../Assests/Images";
import Button from "../../Components/Button";
import CommonInput from "../../Components/Input";
import CommonModal from "../../Components/Modal";
import { useToast } from "../../Components/Toast";
import { setAccessToken } from "../../Services/SessionStore";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  loginUser,
  forgotPassword,
  fetchPublicKey,
} from "../../Store/Reducers/AuthSlice";
import { isValidEmail } from "../../Utils/Constants/RegexConstants";
import {
  getDefaultRouteForRole,
  isRole,
  ROLES,
} from "../../Utils/Constants/RoleConstants";
import { encryptWithPublicKey } from "../../Utils/Helper/clientEncryption";
import {
  saveToLocalStorage,
  STORAGE_KEYS,
} from "../../Utils/Helper/LocalStorage";

import "./style.scss";

const generateSlug = (name: string) => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
};

const Login: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isLoading, forgotPasswordLoading, publicKey, publicKeyLoading } =
    useAppSelector(state => {
      return state.authentication;
    });
  const navigate = useNavigate();
  const toast = useToast();
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });

  const [error, setError] = useState({
    username: "",
    password: "",
  });

  const [isForgotPasswordModalOpen, setIsForgotPasswordModalOpen] =
    useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordError, setForgotPasswordError] = useState("");

  const buildPostLoginPath = (
    roleValue: string | { name?: string } | null | undefined,
    orgSlug?: string | null
  ) => {
    const defaultRoute = getDefaultRouteForRole(roleValue);
    const normalizedSlug = typeof orgSlug === "string" ? orgSlug.trim() : "";

    if (isRole(roleValue, ROLES.IZBA_ADMIN)) {
      return defaultRoute;
    }
    return normalizedSlug ? `/${normalizedSlug}${defaultRoute}` : defaultRoute;
  };

  const getPublicKeyForEncryption = async (): Promise<string | null> => {
    if (publicKey) return publicKey;

    const response = await dispatch(fetchPublicKey());
    if (response.meta.requestStatus === "fulfilled") {
      return (response.payload as string) || null;
    }
    return null;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError({
      ...error,
      [e.target.name]: "",
    });
  };
  const handleSubmit = async () => {
    if (!validateForm()) return;

    const key = await getPublicKeyForEncryption();
    if (!key) {
      toast.error("Unable to fetch encryption key. Please try again.");
      return;
    }

    let encryptedPassword = "";
    try {
      encryptedPassword = await encryptWithPublicKey(key, formData.password);
    } catch (encryptionError) {
      toast.error("Password encryption failed. Please try again.");
      return;
    }

    const result = await dispatch(
      loginUser({
        email: formData.username,
        password: encryptedPassword,
      })
    );
    if (result.meta.requestStatus === "fulfilled") {
      const { access_token } = result.payload.token;
      const {
        user_id,
        email,
        full_name,
        phone,
        role,
        tenant_domain,
        tenant_id,
        is_new_user,
        tenant_name,
        executive_ops,
        executive_ops_approve,
        image_url,
        slug,
        tenant_slug,
        position,
        preferences,
      } = result.payload.user;

      const imageUrl =
        typeof image_url === "string" && image_url.trim()
          ? image_url.trim()
          : "";

      const resolvedSlug =
        typeof slug === "string" && slug.trim()
          ? slug.trim()
          : typeof tenant_slug === "string"
            ? tenant_slug.trim()
            : generateSlug(tenant_name || "");

      const userData = {
        userId: user_id,
        email,
        fullName: full_name,
        phone,
        role,
        tenantDomain: tenant_domain,
        tenantId: tenant_id,
        isNewUser: is_new_user || false,
        tenantName: tenant_name,
        slug: resolvedSlug,
        executiveOps: executive_ops ?? false,
        executiveOpsApprove: executive_ops_approve ?? false,
        imageUrl,
        position: position ?? "",
        preferences: preferences || {},
      };

      setAccessToken(access_token);
      saveToLocalStorage(STORAGE_KEYS.LOGGED_IN_USER, userData);

      return navigate(buildPostLoginPath(role, resolvedSlug));
    }
  };

  const validateForm = () => {
    let isValid = true;
    const newError = { username: "", password: "" };

    if (!formData.username) {
      newError.username = "Username is required.";
      isValid = false;
    }

    if (!formData.password) {
      newError.password = "Password is required.";
      isValid = false;
    }

    setError(newError);
    return isValid;
  };

  const handleForgotPasswordSubmit = async () => {
    setForgotPasswordError("");

    if (!forgotPasswordEmail) {
      setForgotPasswordError("Email address is required.");
      return;
    }

    if (!isValidEmail(forgotPasswordEmail)) {
      setForgotPasswordError("Please enter a valid email address.");
      return;
    }

    const result = await dispatch(
      forgotPassword({
        email: forgotPasswordEmail,
      })
    );

    if (result.meta.requestStatus === "fulfilled") {
      toast.success("Password reset link has been sent to your email address.");
      setIsForgotPasswordModalOpen(false);
      setForgotPasswordEmail("");
    } else if (result.meta.requestStatus === "rejected") {
      const errorMessage =
        (result.payload as string) ||
        "Failed to send reset email. Please try again.";
      toast.error(errorMessage);
    }
  };

  const handleForgotPasswordEmailChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setForgotPasswordEmail(e.target.value);
    setForgotPasswordError("");
  };

  const toggleForgotPasswordModal = () => {
    setIsForgotPasswordModalOpen(!isForgotPasswordModalOpen);
    if (isForgotPasswordModalOpen) {
      setForgotPasswordEmail("");
      setForgotPasswordError("");
    }
  };

  return (
    <div className="login-container">
      <div className="login-content">
        <div className="login-card">
          <div className="login-header">
            <div className="logo-wrapper">
              <img
                src={logo}
                alt=""
                style={{ height: 36, width: 36 }}
                className="rounded-md object-cover"
              />
            </div>
            <h2 className="welcome-back-title">ExecBridge</h2>
            <p className="sign-in-subtitle">Please sign in to continue</p>
          </div>

          <div className="login-form-wrapper">
            <div>
              <div className="input-group">
                <CommonInput
                  name="username"
                  label="Username"
                  type="text"
                  placeholder="Enter username"
                  value={formData.username}
                  onChange={handleChange}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                  className="mb-8 w-full"
                  error={error.username}
                  required
                />
              </div>

              <div className="input-group">
                <CommonInput
                  name="password"
                  label="Password"
                  type="password"
                  placeholder="Enter password"
                  value={formData.password}
                  onChange={handleChange}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                  className="mb-8 w-full"
                  error={error.password}
                  required
                />
              </div>

              <div className="button-wrapper">
                <Button
                  loading={isLoading || publicKeyLoading}
                  onClick={handleSubmit}
                  className="login-button"
                >
                  Sign In
                </Button>
                <Button
                  type="button"
                  onClick={toggleForgotPasswordModal}
                  className="forgot-password-link"
                  variant="white"
                >
                  Forgot Password?
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CommonModal
        isOpen={isForgotPasswordModalOpen}
        toggle={toggleForgotPasswordModal}
        title="Reset Password"
        size="md"
      >
        <div>
          <div className="mb-4">
            <CommonInput
              name="email"
              label="Email address"
              type="email"
              placeholder="Enter your email"
              value={forgotPasswordEmail}
              onChange={handleForgotPasswordEmailChange}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleForgotPasswordSubmit();
                }
              }}
              className="mb-4 w-full"
              error={forgotPasswordError}
              required
            />
          </div>
          <p className="text-sm text-[#727272] mb-6 leading-6">
            Enter your email to receive a password reset link
          </p>
          <div className="flex gap-3 justify-end mt-6">
            <Button
              variant="secondary"
              onClick={toggleForgotPasswordModal}
              className="flex-1 min-w-[120px]"
              type="button"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleForgotPasswordSubmit}
              loading={forgotPasswordLoading}
              icon={<Mail size={16} className="text-white" />}
              className="flex-1 min-w-[120px]"
              type="button"
            >
              Send Reset Email
            </Button>
          </div>
        </div>
      </CommonModal>
    </div>
  );
};

export default Login;
